import React, { useContext, useEffect, useState } from 'react'
import './index.scss'
import { observer } from 'mobx-react'
import CatalogMobileSubLinks from '@Components/Navigations/CatalogMobileSubLinks'
import StatusComponent from '@Components/Status'
import { WORK_STATUSES } from '@Const/work-statuses'
import TabsWrapperComponent, { Tab } from '@Components/TabsWrapperComponent'
import { StoresContext } from '@App/contexts'
import ContentHeader from '@Components/ContentHeader'
import RegistrationHistoryTab from '@Scenes/RightsholderScene/RightsholderCatalogRegistrationHistory/RegistrationHistoryTab'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'
import { RegistrationHistoryTabName } from '@App/enum/registration-history-tab-name'
import { RegistrationStatus } from '@App/enum/registration-status'
import { RegistrationHistoryStatusTypes } from '@App/forms/filters/reg-history-filter'
import NeedsAttentionTabHeader from '@Scenes/RightsholderScene/RightsholderCatalogRegistrationHistory/NeedsAttentionTabHeader'

const RightsholderCatalogRegistrationHistory = () => {
    const { uiStore, registrationHistoryStore } = useContext(StoresContext)
    const [needsAttentionCount, setNeedsAttentionCount] = useState<number | null>(null)

    useEffect(() => {
        analyticsSendPage('rightsholder_registration_history')
        // Fetch the needs attention count
        fetchNeedsAttentionCount().then()
    }, [])

    const fetchNeedsAttentionCount = async () => {
        try {
            const needsAttentionTab = registrationHistoryStore.registrationHistoryTabs.get(
                RegistrationHistoryTabName.NeedsAttention
            )
            if (needsAttentionTab) {
                // Fetch with default pagination and no filters/search
                const registrationStatusAndFilters = {
                    status: RegistrationStatus.needs_attention,
                    defaultFilters: ['processing'] as RegistrationHistoryStatusTypes[],
                }
                await needsAttentionTab.searchRegistrationHistory(registrationStatusAndFilters, { page: 1, limit: 1 })
                const needsAttentionWorksCount = needsAttentionTab.works?.count ?? 0

                // Populate the tab store with all matching rows so the first tab visit does not show a single item.
                if (needsAttentionWorksCount > 1) {
                    await needsAttentionTab.searchRegistrationHistory(registrationStatusAndFilters)
                }

                setNeedsAttentionCount(needsAttentionWorksCount)
            }
        } catch {
            // Error fetching count is non-critical, silently fail
            setNeedsAttentionCount(0)
        }
    }

    const tabs: Tab[] = Array.from(registrationHistoryStore.registrationHistoryTabs, ([key, value]) => ({
        tabName: key.valueOf(),
        tabDisplayName:
            key === RegistrationHistoryTabName.NeedsAttention && needsAttentionCount !== null ? (
                <NeedsAttentionTabHeader needsAttentionCount={needsAttentionCount} />
            ) : (
                key.valueOf()
            ),
        tabComponent: <RegistrationHistoryTab type={key} key={key.valueOf()} registrationHistoryTabStore={value} />,
    }))

    const isMobile = uiStore.isMobile

    return (
        <>
            <title>Registration History</title>
            <div className="rightsholder-catalog-history-wrapper">
                <ContentHeader
                    title="Registration History"
                    descriptionElement={
                        <>
                            <p>
                                Registration History is a record of new work submissions or updates to existing works.
                                Updates or registrations will be saved here for two years from the date of submission.
                            </p>
                            <p>
                                Updated information on an existing work will only show in the corresponding fields. New
                                work submissions will show all fields.
                            </p>
                        </>
                    }
                >
                    <StatusComponent
                        tooltip="Our system has received your suggested submission."
                        withCount={false}
                        status={WORK_STATUSES.SUBMITTED}
                        big
                    />
                    <StatusComponent
                        tooltip="Your submission was approved by our system."
                        withCount={false}
                        status={WORK_STATUSES.ACCEPTED}
                        big
                    />
                    <StatusComponent
                        tooltip="Your submission was rejected by our system."
                        withCount={false}
                        status={WORK_STATUSES.REJECTED}
                        big
                    />
                    <StatusComponent
                        tooltip="Your submission was overwritten by a newer submission."
                        withCount={false}
                        status={WORK_STATUSES.SUPERSEDED}
                        big
                    />
                </ContentHeader>
                <br />
                {isMobile && <CatalogMobileSubLinks />}
                <TabsWrapperComponent tabs={tabs} defaultTab={tabs[0]} />
            </div>
        </>
    )
}

export default observer(RightsholderCatalogRegistrationHistory)
